import type { Attachment } from "@services/bayHireApi";

/**
 * The domain does not track a single "current" attachment per type via a dedicated reference field
 * - attachments are a plain child list on the request, matched by `attachmentType` on read
 * (`BayHireRequestService`, both `create()` and `preApprove()`). Nothing in the domain deletes or
 * replaces an older one when a newer one of the same type is uploaded, so more than one
 * `HOD_APPROVAL` attachment can legitimately exist on a request once Update Request (which reuses
 * the existing per-request upload endpoint - see `BayHireCreateAttachmentUpload.tsx`) lets a vendor
 * re-upload it.
 *
 * Picks the MOST RECENTLY uploaded attachment of the given type (by `uploadedAt`), not the first
 * one in the array - `Array.prototype.find` would surface the OLDEST one instead, silently hiding
 * a re-upload from every screen that reads it (the Documents tab's HOD card, the read-only
 * Pre-approval summary, Update Request's own prefill). Falls back to the last array element when
 * `uploadedAt` is missing, since no ordering guarantee exists on the list itself either.
 */
export const findLatestAttachmentByType = (
  attachments: Attachment[] | null | undefined,
  attachmentType: string,
): Attachment | null => {
  const matches = (attachments ?? []).filter((a) => a.attachmentType === attachmentType);
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];

  const withDates = matches.filter((a) => !!a.uploadedAt);
  if (withDates.length === 0) return matches[matches.length - 1];

  return withDates.reduce((latest, current) =>
    new Date(current.uploadedAt as string) > new Date(latest.uploadedAt as string)
      ? current
      : latest,
  );
};
